import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Tooltip,
  styled,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import BarChartIcon from "@mui/icons-material/BarChart";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import EventIcon from "@mui/icons-material/Event";
import {
  Home as HomeIcon,
  Article as ArticleIcon,
  Help as HelpIcon,
  Settings as SettingsIcon,
  Dashboard as DashboardIcon,
  LibraryBooks as LibraryBooksIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import logo from "./assets/logo.png";
import { useSelector } from "react-redux";

const iconMap = {
  recruitment: <HomeIcon />,
  "organisation structure": <AccountTreeIcon />,
  "organization structure": <AccountTreeIcon />,
  "org structure": <AccountTreeIcon />,
  structure: <AccountTreeIcon />,
  payroll: <ArticleIcon />,
  salary: <ArticleIcon />,
  compensation: <ArticleIcon />,
  rbm: <BarChartIcon />,
  "results based management": <BarChartIcon />,
  "leave and absence": <EventIcon />,
  leave: <EventIcon />,
  absence: <EventIcon />,
  "exit and termination": <EventIcon />,
  termination: <EventIcon />,
  "contract management": <LibraryBooksIcon />,
  contracts: <LibraryBooksIcon />,
  agreements: <LibraryBooksIcon />,
  discipline: <SettingsIcon />,
  disciplinary: <SettingsIcon />,
  training: <DashboardIcon />,
  default: <HelpIcon />,
};

// ---------------- STYLED COMPONENTS ----------------

const SidebarContainer = styled(Box)(({ theme, $expanded, $mobileOpen }) => ({
  width: $expanded ? 280 : 80, // Synced exactly with ArticlesPage margins
  height: "100vh",
  position: "fixed",
  zIndex: theme.zIndex.drawer,
  background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)", // Matches modern SaaS theme
  boxShadow: "4px 0 24px rgba(0, 0, 0, 0.15)",
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  }),
  overflowY: "auto",
  "&::-webkit-scrollbar": {
    width: "4px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    borderRadius: "4px",
  },
  [theme.breakpoints.down("sm")]: {
    width: $mobileOpen ? 280 : 0,
    transform: $mobileOpen ? "translateX(0)" : "translateX(-280px)",
    transition: theme.transitions.create(["width", "transform"], {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard,
    }),
  },
}));

const MenuItemButton = styled(ListItemButton)(
  ({ theme, $expanded, $selected }) => ({
    minHeight: 48,
    justifyContent: $expanded ? "initial" : "center",
    px: 2.5,
    margin: theme.spacing(0.5, 1.5),
    borderRadius: "12px",
    color: $selected ? "#ffffff" : "#94a3b8", // Bright white if selected, muted slate if not
    backgroundColor: $selected ? "rgba(59, 130, 246, 0.15)" : "transparent",
    border: $selected
      ? "1px solid rgba(59, 130, 246, 0.3)"
      : "1px solid transparent",
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.05)",
      color: "#ffffff",
      transform: "translateX(4px)",
    },
  })
);

function TopToolbar({ expanded, toggleExpand, mobileOpen }) {
  const [sections, setSections] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const getIconForCategory = (categoryName) => {
    if (!categoryName) return <HelpIcon />;
    const lowerCaseCategory = categoryName.toLowerCase().trim();
    if (iconMap[lowerCaseCategory]) return iconMap[lowerCaseCategory];
    const cleanCategory = lowerCaseCategory.replace(/[^a-z\s]/g, "");
    if (iconMap[cleanCategory]) return iconMap[cleanCategory];
    const words = cleanCategory.split(/\s+/);
    for (const word of words) {
      if (iconMap[word]) return iconMap[word];
    }
    for (const [key, icon] of Object.entries(iconMap)) {
      if (lowerCaseCategory.includes(key)) return icon;
    }
    return iconMap["default"] || <HelpIcon />;
  };

  const handleClick = (section) => {
    setSelectedItem(section.id);
    navigate(section.link, {
      state: {
        categoryTitle: section.title,
        categoryDescriptions: section.descriptions,
      },
    });
    if (isMobile) {
      toggleExpand();
    }
  };

  const { reduxSections = [] } = useSelector((state) => ({
    reduxSections: state.support.categories || [],
  }));

  useEffect(() => {
    const CompleteReduxSections = reduxSections.map((item) => ({
      id: item.id || "unknown",
      title: item.categoryName || "Untitled",
      descriptions: item.descriptions || "No description provided",
      link: `/ArticlesPage?id=${encodeURIComponent(item.id)}`,
      icon: getIconForCategory(item.categoryName),
    }));
    setSections(CompleteReduxSections);
  }, [reduxSections]);

  // Read URL parameters to set the active item securely
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const currentId = searchParams.get("id");
    
    if (currentId) {
      // Ensure we match types (String to Number) if needed
      setSelectedItem(Number(currentId) || currentId); 
    } else {
      setSelectedItem(null);
    }
  }, [location.search, sections]);

  return (
    <SidebarContainer $expanded={expanded} $mobileOpen={mobileOpen}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: expanded ? "space-between" : "center",
          p: 2,
          mb: 1,
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
        }}
      >
        {expanded && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, ml: 1 }}>
            <img
              src={logo}
              alt="Logo"
              style={{
                width: "40px",
                height: "40px",
                filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
              }}
            />
          </Box>
        )}
        {!isMobile && (
          <IconButton
            onClick={toggleExpand}
            sx={{
              color: "#94a3b8",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)", color: "#fff" },
            }}
          >
            {expanded ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>
        )}
      </Box>

      <List sx={{ pt: 1, fontFamily: "'Inter', sans-serif" }}>
        {sections.map((section) => (
          <ListItem key={section.title} disablePadding>
            <Tooltip
              title={!expanded ? section.title : ""}
              placement="right"
              disableHoverListener={expanded || isMobile}
              arrow
            >
              <MenuItemButton
                onClick={() => handleClick(section)}
                $expanded={expanded || isMobile}
                $selected={selectedItem === section.id}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: expanded || isMobile ? 2.5 : "auto",
                    justifyContent: "center",
                    color: "inherit",
                  }}
                >
                  {React.cloneElement(section.icon, {
                    sx: { fontSize: selectedItem === section.id ? 24 : 22 },
                  })}
                </ListItemIcon>
                {(expanded || isMobile) && (
                  <ListItemText
                    primary={section.title}
                    sx={{
                      "& .MuiTypography-root": {
                        fontSize: "0.9rem",
                        fontWeight: selectedItem === section.id ? 600 : 500,
                        letterSpacing: "0.3px",
                      },
                    }}
                  />
                )}
              </MenuItemButton>
            </Tooltip>
          </ListItem>
        ))}

        <Box sx={{ my: 2 }}>
          <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.08)", mx: 2 }} />
        </Box>

        <ListItem disablePadding>
          <Tooltip
            title={!expanded ? "Close / Home" : ""}
            placement="right"
            disableHoverListener={expanded || isMobile}
            arrow
          >
            <MenuItemButton
              onClick={() => navigate("/")}
              $expanded={expanded || isMobile}
              $selected={false}
              sx={{ color: "#ef4444", "&:hover": { color: "#f87171" } }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: expanded || isMobile ? 2.5 : "auto",
                  justifyContent: "center",
                  color: "inherit",
                }}
              >
                <CloseIcon sx={{ fontSize: 22 }} />
              </ListItemIcon>
              {(expanded || isMobile) && (
                <ListItemText
                  primary="Close"
                  sx={{
                    "& .MuiTypography-root": {
                      fontSize: "0.9rem",
                      fontWeight: 600,
                    },
                  }}
                />
              )}
            </MenuItemButton>
          </Tooltip>
        </ListItem>
      </List>
    </SidebarContainer>
  );
}

export default TopToolbar;